import { Link } from "@tanstack/react-router";
import { MapPin, Phone, Mail, Facebook, Clock, Package } from "lucide-react";



export function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-900 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <div className="w-14 h-14 rounded-xl overflow-hidden flex items-center justify-center bg-white">
                <img
                  src="/logo.svg"
                  alt="Hilltop Preparatory School Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <h3 className="text-xl font-bold">Hilltop</h3>
                <p className="text-xs text-slate-400 -mt-1">
                  Preparatory School
                </p>
              </div>
            </Link>

            <p className="text-slate-400 mb-6 leading-relaxed">
              More than just a place to get an education.
            </p>

            <div className="flex gap-4">
              <a
                href="https://www.facebook.com/100054512497333/about/"
                className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-red-600 transition-colors"
              >
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Our School */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Our School</h4>
            <ul className="space-y-4">
              <li>
                <Link to="/" className="text-slate-400 hover:text-white transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-slate-400 hover:text-white transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/founders-dream" className="text-slate-400 hover:text-white transition-colors">
                  Founder's Dream
                </Link>
              </li>
              <li>
                <Link to="/leadership" className="text-slate-400 hover:text-white transition-colors">
                  Leadership
                </Link>
              </li>
              <li>
                <Link to="/faculty" className="text-slate-400 hover:text-white transition-colors">
                  Faculty
                </Link>
              </li>
              <li>
                <Link to="/performance" className="text-slate-400 hover:text-white transition-colors">
                  Performance
                </Link>
              </li>
            </ul>
          </div>

          {/* Facilities */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Facilities</h4>
            <ul className="space-y-4">
              <li>
                <Link to="/facilities#academics" className="text-slate-400 hover:text-white transition-colors">
                  Academics
                </Link>
              </li>
              <li>
                <Link to="/facilities#extra-curricular" className="text-slate-400 hover:text-white transition-colors">
                  Extra Curricular
                </Link>
              </li>
              <li>
                <Link to="/facilities#boarding" className="text-slate-400 hover:text-white transition-colors">
                  Boarding
                </Link>
              </li>
              <li>
                <a
                  href="mailto:info@hilltop.co.ke?subject=Application%20Request&body=Hello%20Hilltop%20Team,%0D%0A%0D%0AI%20would%20like%20to%20apply."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  Apply Now
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                <span className="text-slate-400">
                  Hilltop Preparatory School, Kenya
                </span>
              </li>

              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-red-500 flex-shrink-0" />
                <a
                  href="tel:+254722374384"
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  +254 722 374 384 (General Line)
                </a>
              </li>

              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-red-500 flex-shrink-0" />
                <a
                  href="tel:+254733374384"
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  +254 733 374 384 (Accounts)
                </a>
              </li>

              <li className="flex items-center gap-3">
                <Package className="w-5 h-5 text-red-500 flex-shrink-0" />
                <span className="text-slate-400">
                  P.O. Box 817-30200, Kitale, Kenya
                </span>
              </li>

              <li className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-red-500 flex-shrink-0" />
                <span className="text-slate-400">
                  Mon – Fri: 8:00 AM – 6:00 PM & Sat-Sun 8:00 AM – 1:00 PM
                </span>
              </li>

              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-red-500 flex-shrink-0" />
                <a
                  href="mailto:info@hilltop.co.ke"
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  info@hilltop.co.ke
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">
              Copyright {currentYear} © Hilltop Preparatory School. All rights
              reserved.
            </p>

            <div className="flex gap-6">
              <a href="#" className="text-slate-500 hover:text-white text-sm transition-colors">
                Privacy Policy
              </a>
              <a href="#" className="text-slate-500 hover:text-white text-sm transition-colors">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
